import InfoIcon from "@mui/icons-material/Info";
import {
  Box,
  FormControlLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import useColors from "../../../../api/hooks/useColors";
import ContainedButton from "../../../../components/buttons/ContainedButton";
import TextButton from "../../../../components/buttons/TextButton";
import CustomToolTip from "../../../../components/CustomTooltip";
import LoadingIcon from "../../../../components/LoadingIcon";
import { CustomCheckbox } from "../../../../components/StyledDataGrid";
import { Toast } from "../../../../components/Toast";
import useWebhookConfig from "./useWebhookConfig";

const SEVERITY_OPTIONS = [
  { value: 1, label: "Low" },
  { value: 2, label: "Medium" },
  { value: 3, label: "High" },
];

const DISABLED_SEVERITY = "none";

const WebhookConfigInternal = ({
  config,
  toolName,
  toolDisplayName,
  updateConfigFn,
  isUpdating,
}) => {
  const colors = useColors();
  const originalDetectionSeverity =
    config?.ticketer_detection_severity || DISABLED_SEVERITY;
  const originalControlSeverity =
    config?.ticketer_control_severity || DISABLED_SEVERITY;

  const [detectionSeverity, setDetectionSeverity] = useState(
    originalDetectionSeverity
  );
  const [controlSeverity, setControlSeverity] = useState(
    originalControlSeverity
  );

  const [lastDetectionSeverity, setLastDetectionSeverity] = useState(
    config?.ticketer_detection_severity || 1
  );
  const [lastControlSeverity, setLastControlSeverity] = useState(
    config?.ticketer_control_severity || 1
  );

  useEffect(() => {
    const newDetectionSeverity =
      config?.ticketer_detection_severity || DISABLED_SEVERITY;
    const newControlSeverity =
      config?.ticketer_control_severity || DISABLED_SEVERITY;

    setDetectionSeverity(newDetectionSeverity);
    setControlSeverity(newControlSeverity);

    if (config?.ticketer_detection_severity) {
      setLastDetectionSeverity(config.ticketer_detection_severity);
    }
    if (config?.ticketer_control_severity) {
      setLastControlSeverity(config.ticketer_control_severity);
    }
  }, [config?.ticketer_detection_severity, config?.ticketer_control_severity]);

  const hasChanges =
    detectionSeverity !== originalDetectionSeverity ||
    controlSeverity !== originalControlSeverity;

  const handleDetectionCheckboxChange = (event) => {
    const enabled = event.target.checked;
    setDetectionSeverity(enabled ? lastDetectionSeverity : DISABLED_SEVERITY);
  };

  const handleDetectionSeverityChange = (event) => {
    const newValue = event.target.value;
    setDetectionSeverity(newValue);
    setLastDetectionSeverity(newValue);
  };

  const handleControlCheckboxChange = (event) => {
    const enabled = event.target.checked;
    setControlSeverity(enabled ? lastControlSeverity : DISABLED_SEVERITY);
  };

  const handleControlSeverityChange = (event) => {
    const newValue = event.target.value;
    setControlSeverity(newValue);
    setLastControlSeverity(newValue);
  };

  const handleSave = () => {
    const newConfig = {
      ...(detectionSeverity !== DISABLED_SEVERITY
        ? { ticketer_detection_severity: detectionSeverity }
        : {}),
      ...(controlSeverity !== DISABLED_SEVERITY
        ? { ticketer_control_severity: controlSeverity }
        : {}),
    };

    updateConfigFn(newConfig)
      .then(() => {
        Toast.success("Configuration updated successfully");
      })
      .catch((error) => {
        const errorDetail =
          error?.response?.data?.detail ||
          error?.response?.data?.message ||
          error?.message;
        if (errorDetail) {
          Toast.error(`Failed to update: ${errorDetail}`);
        } else {
          Toast.error("Failed to update configuration");
        }
      });
  };

  const handleCancel = () => {
    setDetectionSeverity(originalDetectionSeverity);
    setControlSeverity(originalControlSeverity);
    setLastDetectionSeverity(config?.ticketer_detection_severity || 1);
    setLastControlSeverity(config?.ticketer_control_severity || 1);
  };

  return (
    <Box width={600}>
      <Typography variant="h5" gutterBottom>
        Configure Auto-Ticketing for {toolDisplayName}
      </Typography>

      <Box mt={3}>
        <FormControlLabel
          control={
            <CustomCheckbox
              checked={detectionSeverity !== DISABLED_SEVERITY}
              onChange={handleDetectionCheckboxChange}
              disabled={isUpdating}
              sx={{
                "&.Mui-checked": {
                  color: colors.primary_main,
                },
              }}
            />
          }
          label={
            <Box sx={{ display: "flex", alignItems: "center" }}>
              Enable Detection Auto-Ticketing
              <CustomToolTip title="When enabled, detections meeting the severity threshold will automatically create tickets">
                <Box
                  component="span"
                  sx={{ ml: 1, display: "inline-flex", alignItems: "center" }}
                >
                  <InfoIcon
                    fontSize="small"
                    sx={{ color: colors.primary_main }}
                  />
                </Box>
              </CustomToolTip>
            </Box>
          }
        />

        {detectionSeverity !== DISABLED_SEVERITY && (
          <Box ml={4} mt={1}>
            <Typography variant="body2" gutterBottom>
              Minimum Detection Severity:
            </Typography>
            <Select
              value={detectionSeverity}
              onChange={handleDetectionSeverityChange}
              disabled={isUpdating}
              size="small"
              sx={{ minWidth: 150 }}
            >
              {SEVERITY_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </Box>
        )}
      </Box>

      <Box mt={3}>
        <FormControlLabel
          control={
            <CustomCheckbox
              checked={controlSeverity !== DISABLED_SEVERITY}
              onChange={handleControlCheckboxChange}
              disabled={isUpdating}
              sx={{
                "&.Mui-checked": {
                  color: colors.primary_main,
                },
              }}
            />
          }
          label={
            <Box sx={{ display: "flex", alignItems: "center" }}>
              Enable Control Auto-Ticketing
              <CustomToolTip title="When enabled, control gaps meeting the severity threshold will automatically create tickets">
                <Box
                  component="span"
                  sx={{ ml: 1, display: "inline-flex", alignItems: "center" }}
                >
                  <InfoIcon
                    fontSize="small"
                    sx={{ color: colors.primary_main }}
                  />
                </Box>
              </CustomToolTip>
            </Box>
          }
        />

        {controlSeverity !== DISABLED_SEVERITY && (
          <Box ml={4} mt={1}>
            <Typography variant="body2" gutterBottom>
              Minimum Control Severity:
            </Typography>
            <Select
              value={controlSeverity}
              onChange={handleControlSeverityChange}
              disabled={isUpdating}
              size="small"
              sx={{ minWidth: 150 }}
            >
              {SEVERITY_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </Box>
        )}
      </Box>

      <Box mt={4} display="flex" gap={2}>
        <ContainedButton
          onClick={handleSave}
          disabled={!hasChanges || isUpdating}
          color="primary"
          size="medium"
        >
          Save Changes
        </ContainedButton>
        <TextButton
          onClick={handleCancel}
          disabled={!hasChanges || isUpdating}
          color="inherit"
          size="medium"
        >
          Cancel
        </TextButton>
      </Box>
    </Box>
  );
};

const WebhookConfig = ({ toolName, toolDisplayName }) => {
  const { config, isLoading, updateConfig, isUpdating } =
    useWebhookConfig(toolName);

  if (isLoading) {
    return <LoadingIcon size={25} />;
  }

  return (
    <WebhookConfigInternal
      config={config}
      toolName={toolName}
      toolDisplayName={toolDisplayName}
      updateConfigFn={updateConfig}
      isUpdating={isUpdating}
    />
  );
};

export default WebhookConfig;
